import React, { useCallback } from 'react'
import AsyncSelect from 'react-select/async'
import { api } from '../services/api'
import { useDebouncedCallback } from '../hooks'
import selectStyles from '../styles/react-select.module.scss'
import { cupTeamName } from '../utils/cupTeams'

export interface CupTeamOption {
  tc_cup_team_id: string
  tc_name: string | null
  ext_name: string | null
  country_code: string | null
}

export interface CupTeamSelectProps {
  value: CupTeamOption | null
  onChange: (team: CupTeamOption | null) => void
  excludeId?: string
  placeholder?: string
  isDisabled?: boolean
  inputId?: string
}

const CupTeamSelect: React.FC<CupTeamSelectProps> = ({
  value,
  onChange,
  excludeId,
  placeholder = 'Search cup teams...',
  isDisabled = false,
  inputId,
}) => {
  const fetchTeams = useCallback(
    async (inputValue: string): Promise<CupTeamOption[]> => {
      if (inputValue.length < 2) return []
      try {
        const response = await api.get<{ cup_teams: CupTeamOption[] }>('/cup-teams', { name: inputValue })
        return (response?.cup_teams || []).filter((t) => t.tc_cup_team_id !== excludeId)
      } catch {
        return []
      }
    },
    [excludeId],
  )
  const loadTeams = useDebouncedCallback(fetchTeams, 300)

  return (
    <AsyncSelect<CupTeamOption, false>
      inputId={inputId}
      value={value}
      onChange={(option) => onChange(option ?? null)}
      loadOptions={loadTeams}
      defaultOptions={false}
      openMenuOnFocus={false}
      openMenuOnClick={false}
      isClearable
      isDisabled={isDisabled}
      placeholder={placeholder}
      className={selectStyles.reactSelect}
      classNamePrefix="react-select"
      getOptionValue={(option) => option.tc_cup_team_id}
      getOptionLabel={(option) => (option.country_code ? `${cupTeamName(option)} (${option.country_code})` : cupTeamName(option))}
      noOptionsMessage={({ inputValue }) =>
        inputValue.length < 2 ? 'Type at least 2 characters to search...' : 'No cup teams found'
      }
    />
  )
}

export default CupTeamSelect
